<script setup lang="ts">
  // Compact money strip shown instead of the full money pane on short
  // viewports: due amount / paid badge, a Detalle toggle for the pane
  // overlay, and the payment actions (via usePosOrderPayments).
  import { computed, getCurrentInstance, inject, ref, Ref } from 'vue';
  import { useElementSize } from '@vueuse/core';
  import { OrderPaymentStatus } from '@/enums/OrderEnums';
  import { IOrder } from '@/interfaces/IOrder';
  import { roundPrice } from '@/utils';
  import { getClubMemberPanelState } from '@/domain/club/club-member-panel';
  import {
    getMoneyStripLayout,
    TMoneyStripClubSlot,
  } from '@/domain/pos/pos-compaction';
  import { PermissionType } from '@/enums/PermissionType';
  import { useAuth } from '@/composition/use-auth';
  import { ORDER_CLUB_CONTEXT_KEY } from '@/composition/use-order-club-context';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { usePosOrderPayments } from '@/composition/use-pos-order-payments';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';

  const emit = defineEmits<{
    (e: 'toggle-detail'): void;
  }>();

  const order = inject<Ref<IOrder>>('order')!;
  const posV2ViewMode = inject<'interactive' | 'preview'>(
    'posV2ViewMode',
    'interactive'
  );
  const clubContext = inject(ORDER_CLUB_CONTEXT_KEY, null);

  const auth = useAuth();
  const money = usePosMoney();
  const restaurantStore = useRestaurantStore();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }
  const {
    isPendingOrder,
    doPayment,
    payWithClubBalance,
    showAddDiscountModal,
    clubRedeemAvailable,
    showRedeemPointsModal,
    clubAttachAvailable,
    showAttachClubModal,
    showTipModal,
  } = usePosOrderPayments(order, clubContext);

  const rootRef = ref<HTMLElement>();
  const { width: stripWidth } = useElementSize(rootRef);

  //#region Club member actions (figure-carrying buttons)

  // Needs the live context: the order embed alone has no wallet figure.
  const clubMember = computed(() =>
    order.value?.clubMember ? clubContext?.member.value ?? null : null
  );
  // The slot is claimed while the figures load so the strip never flashes
  // the legacy Puntos button and re-lays out once the context arrives.
  const clubLoading = computed(
    () =>
      !!order.value?.clubMember &&
      !clubMember.value &&
      !!clubContext?.loading.value
  );
  const clubState = computed(() => {
    const member = clubMember.value;
    if (!member) return null;
    return getClubMemberPanelState({
      program: restaurantStore.restaurant?.club,
      branchId: restaurantStore.selectedBranch?.id,
      member,
      order: order.value,
    });
  });

  const clubPointsFigure = computed(() => {
    const member = clubMember.value;
    const state = clubState.value;
    if (!member || !state || state.points.reason === 'disabled') return null;
    return {
      usable: state.points.usable,
      value: money.formatComputedMoney(state.points.value),
      points: `${member.pointsBalance} ${i18nT('posV2.clubPointsSuffix')}`,
    };
  });

  const clubBalanceFigure = computed(() => {
    const member = clubMember.value;
    const state = clubState.value;
    if (!member || !state || state.balance.reason === 'disabled') return null;
    return {
      usable: state.balance.usable,
      value:
        member.cashTotal > 0
          ? money.formatComputedMoney(member.cashTotal)
          : i18nT('posV2.clubNoBalance'),
    };
  });

  // Only when something actually renders in the club slot: a member whose
  // program disables both resources here must not widen the layout budget.
  const hasClubActions = computed(
    () =>
      clubLoading.value || !!clubPointsFigure.value || !!clubBalanceFigure.value
  );

  //#endregion

  // Member-less order: the club slot holds the "Club" identify button.
  const clubSlot = computed<TMoneyStripClubSlot>(() => {
    if (hasClubActions.value) return 'actions';
    if (clubAttachAvailable.value) return 'attach';
    return 'none';
  });

  // 0 = unmeasured pre-mount; must not flash the overflow menu.
  const layout = computed(() =>
    getMoneyStripLayout(stripWidth.value, clubSlot.value)
  );
  const showInlineClubAttach = computed(
    () => clubSlot.value === 'attach' && layout.value.inlineClubAttach
  );
  const showOverflowClubAttach = computed(
    () => clubSlot.value === 'attach' && !layout.value.inlineClubAttach
  );
  // Legacy Puntos entry: a member whose program offers neither resource here.
  const showRedeemFallback = computed(
    () => clubRedeemAvailable.value && clubSlot.value === 'none'
  );

  const isPaid = computed(
    () => order.value?.paymentStatus === OrderPaymentStatus.Paid
  );

  const dueAmount = computed(() =>
    roundPrice((order.value?.total ?? 0) - (order.value?.totalPaid ?? 0))
  );

  // Same OrdersManage gate the full money pane applies to Pagar/Descuento/
  // Propina: the interactive order modal is also reachable by
  // OrdersHistoric-only users (server rejects the mutations anyway).
  const canManageOrders = computed(() =>
    auth.hasPermission(PermissionType.OrdersManage)
  );

  const showActions = computed(
    () =>
      isPendingOrder.value &&
      posV2ViewMode === 'interactive' &&
      canManageOrders.value
  );

  function runMoreAction(close: () => void, action: () => void) {
    close();
    action();
  }

  defineExpose({ rootRef });
</script>

<template>
  <div ref="rootRef" class="pos-v2-view-money-strip">
    <div class="strip-amount">
      <template v-if="isPaid">
        <PosUiIcon name="check-circle" class="paid-icon" />
        <span class="paid-label">{{ $t('posV2.paidBadge') }}</span>
      </template>
      <template v-else>
        <span class="amount-label">{{ $t('posV2.remaining') }}</span>
        <span class="amount-value">
          {{ money.formatComputedMoney(dueAmount) }}
        </span>
      </template>
    </div>

    <PosUiButton
      color="neutral"
      variant="outline"
      size="sm"
      class="strip-detail-button"
      @click="emit('toggle-detail')"
    >
      {{ $t('posV2.chipDetails') }}
    </PosUiButton>

    <span class="strip-spacer" />

    <template v-if="showActions">
      <!-- Attached member: the club actions carry the live figures and stay
           inline; the words drop first on narrow strips (icon + amount). -->
      <template v-if="hasClubActions">
        <PosUiSkeleton v-if="clubLoading" class="club-skeleton" />
        <PosUiButton
          v-if="clubPointsFigure && clubPointsFigure.usable"
          color="primary"
          variant="solid"
          size="sm"
          icon="star-circle"
          class="club-action"
          :title="$t('posV2.clubUsePoints')"
          @click="showRedeemPointsModal"
        >
          <span v-show="layout.showClubLabels" class="club-action-label">
            {{ $t('posV2.clubPoints') }}
          </span>
          <span class="club-action-value">{{ clubPointsFigure.value }}</span>
          <span v-show="layout.showClubSub" class="club-action-sub">
            {{ clubPointsFigure.points }}
          </span>
        </PosUiButton>
        <span
          v-else-if="clubPointsFigure"
          class="club-figure"
          :title="$t('posV2.clubPoints')"
        >
          <PosUiIcon name="star-circle" class="club-figure-icon" />
          <span v-show="layout.showClubLabels" class="club-action-label">
            {{ $t('posV2.clubPoints') }}
          </span>
          <span class="club-figure-value">{{ clubPointsFigure.points }}</span>
        </span>

        <PosUiButton
          v-if="clubBalanceFigure && clubBalanceFigure.usable"
          color="success"
          variant="solid"
          size="sm"
          icon="wallet"
          class="club-action"
          :title="$t('posV2.clubUseBalance')"
          @click="payWithClubBalance"
        >
          <span v-show="layout.showClubLabels" class="club-action-label">
            {{ $t('posV2.clubBalance') }}
          </span>
          <span class="club-action-value">{{ clubBalanceFigure.value }}</span>
        </PosUiButton>
        <span
          v-else-if="clubBalanceFigure"
          class="club-figure"
          :title="$t('posV2.clubBalance')"
        >
          <PosUiIcon name="wallet" class="club-figure-icon" />
          <span v-show="layout.showClubLabels" class="club-action-label">
            {{ $t('posV2.clubBalance') }}
          </span>
          <span class="club-figure-value">{{ clubBalanceFigure.value }}</span>
        </span>
      </template>

      <!-- Member-less order: the club slot links one (the panel's row in
           strip form). It outlives Descuento/Propina on narrow strips. -->
      <PosUiButton
        v-if="showInlineClubAttach"
        color="neutral"
        variant="outline"
        size="sm"
        icon="star-circle"
        class="club-attach"
        :title="$t('posV2.clubIdentify')"
        @click="showAttachClubModal"
      >
        {{ $t('posV2.metaClub') }}
      </PosUiButton>

      <template v-if="layout.inlineActions">
        <PosUiButton
          color="neutral"
          variant="outline"
          size="sm"
          @click="showAddDiscountModal"
        >
          {{ $t('posV2.discount') }}
        </PosUiButton>
        <PosUiButton
          v-if="showRedeemFallback"
          color="neutral"
          variant="outline"
          size="sm"
          @click="showRedeemPointsModal"
        >
          {{ $t('posV2.clubRedeem') }}
        </PosUiButton>
        <PosUiButton
          color="neutral"
          variant="outline"
          size="sm"
          @click="showTipModal"
        >
          {{ $t('posV2.tip') }}
        </PosUiButton>
      </template>
      <PosUiPopover v-else placement="top" align="end" anchor="trigger">
        <template #trigger="{ toggle }">
          <PosUiButton
            icon="dots-horizontal"
            color="neutral"
            variant="outline"
            size="sm"
            :aria-label="$t('posV2.moreActions')"
            @click="toggle"
          />
        </template>
        <template #default="{ close }">
          <div class="strip-more-actions">
            <button
              type="button"
              class="more-action"
              @click="runMoreAction(close, showAddDiscountModal)"
            >
              {{ $t('posV2.discount') }}
            </button>
            <button
              v-if="showOverflowClubAttach"
              type="button"
              class="more-action"
              @click="runMoreAction(close, showAttachClubModal)"
            >
              {{ $t('posV2.clubIdentify') }}
            </button>
            <button
              v-if="showRedeemFallback"
              type="button"
              class="more-action"
              @click="runMoreAction(close, showRedeemPointsModal)"
            >
              {{ $t('posV2.clubRedeem') }}
            </button>
            <button
              type="button"
              class="more-action"
              @click="runMoreAction(close, showTipModal)"
            >
              {{ $t('posV2.tip') }}
            </button>
          </div>
        </template>
      </PosUiPopover>

      <PosUiButton
        v-if="!isPaid"
        color="success"
        variant="solid"
        size="sm"
        @click="doPayment"
      >
        {{ $t('posV2.pay') }}
      </PosUiButton>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-view-money-strip {
    @apply tw-flex tw-items-center tw-gap-2 tw-border-t tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2;

    .strip-amount {
      // Never lets the flex layout squeeze "$ 1.000" onto two lines: the
      // club buttons shed detail instead (see getMoneyStripLayout).
      @apply tw-flex tw-flex-none tw-items-baseline tw-gap-1.5 tw-whitespace-nowrap;

      .paid-icon {
        @apply tw-self-center tw-text-lg tw-text-emerald-600;
      }

      .paid-label {
        @apply tw-text-sm tw-font-semibold tw-text-emerald-700;
      }

      .amount-label {
        @apply tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-neutral-500;
      }

      .amount-value {
        @apply tw-text-base tw-font-bold tw-tabular-nums tw-text-neutral-900;
      }
    }

    .strip-spacer {
      @apply tw-flex-1;
    }

    // Placeholder for the two club buttons while their figures load.
    .club-skeleton {
      @apply tw-h-9 tw-w-40 tw-flex-shrink-0;
    }

    // "Club" identify button: the brand star marks the slot as the Club's.
    .club-attach {
      @apply tw-flex-shrink-0;

      :deep(.pos-ui-button-icon) {
        @apply tw-text-orange-600;
      }
    }

    // Figure-carrying club buttons: "Puntos $3.500 (350 pts)" / "Saldo $8.000".
    .club-action {
      @apply tw-flex-shrink-0 tw-tabular-nums;

      .club-action-label {
        @apply tw-mr-1 tw-font-medium;
      }

      .club-action-value {
        @apply tw-font-bold;
      }

      .club-action-sub {
        @apply tw-ml-1 tw-text-[11px] tw-font-medium tw-opacity-80;
      }
    }

    // Non-usable resource: quiet neutral pill with the figure only.
    .club-figure {
      @apply tw-inline-flex tw-h-9 tw-flex-shrink-0 tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-neutral-50 tw-px-2.5 tw-text-sm tw-tabular-nums tw-text-neutral-500;

      .club-figure-icon {
        @apply tw-text-base tw-text-neutral-400;
      }

      .club-action-label {
        @apply tw-font-medium;
      }

      .club-figure-value {
        @apply tw-font-semibold tw-text-neutral-600;
      }
    }

    .strip-more-actions {
      @apply tw-flex tw-flex-col tw-gap-1;

      .more-action {
        @apply tw-flex tw-min-h-[40px] tw-items-center tw-rounded-md tw-px-3 tw-text-left tw-text-sm tw-text-neutral-700 tw-transition-colors hover:tw-bg-neutral-50;
      }
    }
  }
</style>
